/**
 * A small accessible on/off switch (role="switch").
 *
 * Used for inline enable/disable actions where opening a modal would be
 * overkill — e.g. pausing an application from a table row without deleting it.
 * Controlled: the parent owns `checked` and applies the change in `onChange`.
 * Pass `busy` while the change is in flight to disable + dim it.
 */
interface ToggleSwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  busy?: boolean;
  /** Accessible label (there is no visible text). */
  label: string;
  title?: string;
}

const ToggleSwitch = ({ checked, onChange, disabled = false, busy = false, label, title }: ToggleSwitchProps) => {
  const off = disabled || busy;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={title ?? label}
      disabled={off}
      onClick={() => onChange(!checked)}
      className={[
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
        checked ? 'bg-success-500' : 'bg-gray-300',
        off ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-4' : 'translate-x-0.5',
        ].join(' ')}
      />
    </button>
  );
};

export default ToggleSwitch;
